import { onBeforeUnmount, onMounted, reactive, toRef, type Ref } from 'vue';

type CookieInformationApi = {
  getConsentGivenFor?: (category: string) => boolean | null | undefined;
  changeCategoryConsentDecision?: (category: string) => void;
  submitConsent?: () => void;
};

function getCookieInformation(): CookieInformationApi | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return (window as Window & { CookieInformation?: CookieInformationApi }).CookieInformation;
}

export const COOKIE_CONSENT_CATEGORIES = ['marketing', 'statistic', 'functional'] as const;

export type CookieConsentCategory = (typeof COOKIE_CONSENT_CATEGORIES)[number];

/** CookieInformation exposes consent under `cookie_cat_<category>` keys. */
export const toCookieInformationKey = (category: CookieConsentCategory) => `cookie_cat_${category}`;

const CONSENT_EVENTS = ['CookieInformationConsentGiven', 'CookieInformationAPIReady'] as const;

/** Shared reactive consent — one source of truth for all `useCookieConsent` callers. */
const consentState = reactive<Record<CookieConsentCategory, boolean>>({
  marketing: false,
  statistic: false,
  functional: false,
});

type ConsentChangeListener = () => void;

let windowListenersAttached = false;
let subscriberCount = 0;
const changeListeners = new Set<ConsentChangeListener>();

/**
 * Raw SDK value (`true` / `false` / `null` / `undefined`) so callers can
 * distinguish “no decision yet” from an explicit deny.
 */
export function getCookieConsentGiven(
  category: CookieConsentCategory,
): boolean | null | undefined {
  return getCookieInformation()?.getConsentGivenFor?.(toCookieInformationKey(category));
}

function syncConsentState() {
  for (const category of COOKIE_CONSENT_CATEGORIES) {
    consentState[category] = !!getCookieConsentGiven(category);
  }

  changeListeners.forEach((listener) => {
    listener();
  });
}

function attachWindowListeners() {
  if (typeof window === 'undefined' || windowListenersAttached) {
    return;
  }

  for (const event of CONSENT_EVENTS) {
    window.addEventListener(event, syncConsentState);
  }
  windowListenersAttached = true;
  syncConsentState();
}

function detachWindowListeners() {
  if (typeof window === 'undefined' || !windowListenersAttached || subscriberCount > 0) {
    return;
  }

  for (const event of CONSENT_EVENTS) {
    window.removeEventListener(event, syncConsentState);
  }
  windowListenersAttached = false;
}

/**
 * App-level subscription (plugins, non-Vue code). Shares the same window
 * listeners as `useCookieConsent` — call once; returns an unsubscribe fn.
 */
export function subscribeCookieConsent(onChange?: ConsentChangeListener): () => void {
  attachWindowListeners();
  subscriberCount += 1;

  if (onChange) {
    changeListeners.add(onChange);
    onChange();
  }

  return () => {
    if (onChange) {
      changeListeners.delete(onChange);
    }
    subscriberCount = Math.max(0, subscriberCount - 1);
    detachWindowListeners();
  };
}

/**
 * Reactively tracks CookieInformation consent for one or more categories.
 *
 * Every requested category starts `false` (privacy-safe default) so gated
 * behaviour never runs before consent is positively confirmed, and stays in
 * sync when the visitor changes their decision (`CookieInformationConsentGiven`)
 * or when the SDK finishes loading (`CookieInformationAPIReady`).
 *
 * Multiple callers share a single pair of window listeners.
 *
 * @example
 * const { functional } = useCookieConsent('functional');
 * const { marketing, statistic } = useCookieConsent(['marketing', 'statistic']);
 */
export const useCookieConsent = <T extends CookieConsentCategory>(
  categories: T | T[],
): Record<T, Ref<boolean>> => {
  const requested = Array.isArray(categories) ? categories : [categories];

  const consent = {} as Record<T, Ref<boolean>>;
  for (const category of requested) {
    consent[category] = toRef(consentState, category);
  }

  let unsubscribe: (() => void) | undefined;

  onMounted(() => {
    unsubscribe = subscribeCookieConsent();
  });

  onBeforeUnmount(() => {
    unsubscribe?.();
    unsubscribe = undefined;
  });

  return consent;
};
